import React, { useContext, useState, useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  Navigate,
  useLocation,
} from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Container,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Box,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem,
  Badge,
  Link as MuiLink,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import PeopleIcon from '@mui/icons-material/People';
import BusinessIcon from '@mui/icons-material/Business';
import ReceiptIcon from '@mui/icons-material/Receipt';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CollectionsIcon from '@mui/icons-material/Collections';
import CategoryIcon from '@mui/icons-material/Category';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import BarChartIcon from '@mui/icons-material/BarChart';
import MenuIcon from '@mui/icons-material/Menu';
import BlockIcon from '@mui/icons-material/Block';
import AccountCircle from '@mui/icons-material/AccountCircle';
import AssignmentIcon from '@mui/icons-material/Assignment';
import DescriptionIcon from '@mui/icons-material/Description';
import LogoutIcon from '@mui/icons-material/Logout';
import Users from './pages/Users';
import Organizations from './pages/Organizations';
import Orders from './pages/Orders';
import NewOrder from './pages/NewOrder';
import PurchaseKits from './pages/PurchaseKits';
import Patients from './pages/Patients';
import Results from './pages/Results';
import Products from './pages/Products';
import Collections from './pages/Collections';
import CollectionEventDetail from './pages/CollectionEventDetail';
import Practice from './pages/Practice';
import Transactions from './pages/Transactions';
import AllTransactions from './pages/AllTransactions';
import AuthContext from './contexts/AuthContext';
import { usePractice } from './contexts/PracticeContext';
import { useFeatureFlags } from './contexts/FeatureFlagsContext';
import PracticeSelector from './components/PracticeSelector';
import NewUser from './pages/NewUser';
import SetupPractice from './pages/SetupPractice';
import SetupPracticeRequests from './pages/SetupPracticeRequests';
import FormTemplates from './pages/FormTemplates';
import Agreements from './pages/Agreements';
import DrawerContent from './components/DrawerContent';
import Footer from './components/Footer';
import OrgsUsers from './pages/OrgsUsers';
import OrganizationUsersPage from './pages/OrganizationUsersPage';
import Profile from './pages/Profile';
import Home from './pages/Home';
import DisabledUser from './pages/DisabledUser';
import './App.css';

function getMenuItems(user, authData, selectedPractice, flags) {
  if (!user) return [];

  if (user.role === 'Disabled') {
    return [
      { text: 'Disabled User', icon: <BlockIcon />, path: '/disabled' }
    ];
  }

  if (user.role === "Unassigned") {
    return [
      { text: 'New user', icon: <PeopleIcon />, path: '/new-user' }
    ];
  }

  // Pending providers and office staff self-enrollment should see setup-practice
  if (["PendingProvider", "PendingOfficeStaff"].includes(user.role)) {
    return [
      { text: 'Set up practice', icon: <BusinessIcon />, path: '/setup-practice' }
    ];
  }

  if (user.role === "Manager") {
    return [
      { text: 'Home', icon: <HomeIcon />, path: '/' },
      { text: 'Users', icon: <PeopleIcon />, path: '/users' },
      // Only show Products and Orders in phase2
      ...(flags.phase2
        ? [
            { text: 'Products', icon: <CategoryIcon />, path: '/products' },
            { text: 'Orders', icon: <ReceiptIcon />, path: '/orders' }
          ]
        : []),
      { text: 'Organizations', icon: <BusinessIcon />, path: '/organizations' },
      { text: 'Setup practice requests', icon: <AssignmentIcon />, path: '/setup-practice-requests' },
      { text: 'All Transactions', icon: <ReceiptLongIcon />, path: '/all-transactions' },
      { text: 'Agreements', icon: <DescriptionIcon />, path: '/agreements' },
      ...(flags.phase3
        ? [
            { text: 'Form templates', icon: <DescriptionIcon />, path: '/form-templates' }
          ]
        : []),
    ];
  }

  if (user.role === "Personnel") {
    // Determine this user's role in the selected organization
    const orgId = selectedPractice?.id;
    const rolesAtOrgs = authData?.roles_at_organizations || [];
    const roleObj = rolesAtOrgs.find(r => r.organization_id === orgId);
    const orgRole = roleObj?.role;
    switch (orgRole) {
      case 'Provider': {
        const items = [
          { text: 'Home', icon: <HomeIcon />, path: '/' },
      { text: 'Practice', icon: <BusinessIcon />, path: '/practice' },
      { text: 'Purchase kits', icon: <AddShoppingCartIcon />, path: '/purchase-kits' },
      ...(flags.phase2
        ? [
            { text: 'New order', icon: <AddShoppingCartIcon />, path: '/new-order' },
            { text: 'Orders', icon: <ReceiptIcon />, path: '/orders' },
            { text: 'Patients', icon: <LocalHospitalIcon />, path: '/patients' },
          ]
        : []),
      ...(flags.phase3
        ? [{ text: 'Collections', icon: <CollectionsIcon />, path: '/collections' }]
        : []),
    ];
    return items;
  }
      case 'OfficeStaff': {
        const items = [
          { text: 'Home', icon: <HomeIcon />, path: '/' },
          { text: 'Practice', icon: <BusinessIcon />, path: '/practice' },
          { text: 'Purchase kits', icon: <AddShoppingCartIcon />, path: '/purchase-kits' },
          ...(flags.phase2
            ? [
                { text: 'New order', icon: <AddShoppingCartIcon />, path: '/new-order' },
                { text: 'Orders', icon: <ReceiptIcon />, path: '/orders' },
                { text: 'Patients', icon: <LocalHospitalIcon />, path: '/patients' },
              ]
            : []),
        ];
        return items;
      }
      case 'Phlebotomist':
        return [
          { text: 'Home', icon: <HomeIcon />, path: '/' },
          { text: 'Practice', icon: <BusinessIcon />, path: '/practice' },
          { text: 'Collections', icon: <CollectionsIcon />, path: '/collections' }
        ];
      default:
        // Fallback menu for other Personnel roles
        return [
          { text: 'Home', icon: <HomeIcon />, path: '/' },
          { text: 'Practice', icon: <BusinessIcon />, path: '/practice' }
        ];
    }
  }

  if (user.role === "Patient") {
    return [
      { text: 'Home', icon: <HomeIcon />, path: '/' },
      { text: 'Results', icon: <BarChartIcon />, path: '/results' }
    ];
  }

  return [];
}

function LoggedOut() {
  const { login } = useContext(AuthContext);
  return (
    <div className="App-header">
      <Typography variant="h4" component="h1" gutterBottom>
        You have been successfully logged out
      </Typography>
      <Button
        variant="contained"
        color="primary"
        onClick={login}
        sx={{ mt: 2 }}
      >
        Log in again
      </Button>
    </div>
  );
}

function PublicHome({ onLogin }) {
  return (
    <Box
      sx={{
        maxWidth: 'md',
        mx: 'auto',
        textAlign: 'left',
        pt: { xs: 2, md: 3 },
        pb: { xs: 1, md: 1.5 },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
          mb: 4,
        }}
      >
        <Typography
          variant="h3"
          component="h1"
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          VeraBIND Test Ordering System
        </Typography>
        <Box
          component="img"
          src="/veravas_logo_178.png"
          alt="Veravas logo"
          sx={{
            height: { xs: 24, sm: 32 },
            width: 'auto',
            objectFit: 'contain',
          }}
        />
      </Box>
      <Typography paragraph sx={{ textTransform: 'none' }}>
        Welcome to the VeraBIND Test Ordering System, the test order management system for the exclusive use of providers under contract with Veravas, Inc. to order our VeraBIND Test Kits.
      </Typography>
      <Typography paragraph sx={{ textTransform: 'none' }}>
        If you have completed the Lab Services Agreement, please create your account or log in below.
      </Typography>
      <Typography paragraph sx={{ textTransform: 'none' }}>
        If you are interested in Veravas test kits, please go to{' '}
        <MuiLink
          href="https://www.veravas.com"
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            color: '#0B6D6D',
            fontWeight: 600,
            '&:hover': { color: '#084F4F' },
          }}
        >
          www.veravas.com
        </MuiLink>{' '}
        to learn more about our products and contact our sales team. If you are a patient or not a provider authorized to order our tests, you can learn more about our tests at{' '}
        <MuiLink
          href="https://www.veravas.com"
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            color: '#0B6D6D',
            fontWeight: 600,
            '&:hover': { color: '#084F4F' },
          }}
        >
          www.veravas.com
        </MuiLink>
        . At this point, we do not support patient access to our portal.
      </Typography>
      <Button variant="contained" color="primary" onClick={onLogin} sx={{ mt: 2 }}>
        Log In or Create Account
      </Button>
    </Box>
  );
}

function PrivateRoute({ children }) {
  // Core hooks must be called unconditionally at top
  const { user, tokens, authData } = useContext(AuthContext);
  const location = useLocation();
  const { selectedPractice } = usePractice();
  const flags = useFeatureFlags();

  // If no auth tokens, redirect to home/login
  if (!tokens) {
    return <Navigate to="/" replace />;
  }
  // If authenticated but user data is still loading, show spinner
  if (tokens && !user) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }
  // User is loaded: determine permitted menu items (for access control)
  let permittedMenuItems;
  if (
    user.role === 'Personnel' &&
    authData?.organizations?.length > 0 &&
    !selectedPractice
  ) {
    // Before a practice is selected, gather items across all organizations
    const allOrgItems = authData.organizations.flatMap(org =>
      getMenuItems(user, authData, org, flags)
    );
    // Deduplicate items by path
    const seen = new Set();
    permittedMenuItems = allOrgItems.filter(item => {
      if (seen.has(item.path)) return false;
      seen.add(item.path);
      return true;
    });
  } else {
    // Normal case: use items for selected practice or other roles
    permittedMenuItems = getMenuItems(user, authData, selectedPractice, flags);
  }
  // Always allow home, profile, and logged_out, plus permitted menu paths
  const allowedPaths = [
    '/',
    '/profile',
    '/logged_out',
    ...permittedMenuItems.map(item => item.path),
  ];
  // Check if current path exactly matches an allowed path, or is a sub-route
  const isAllowed = allowedPaths.some(pth => {
    if (pth === '/') {
      return location.pathname === '/';
    }
    return location.pathname === pth || location.pathname.startsWith(pth + '/');
  });
  if (!isAllowed) {
    return <Navigate to="/" replace />;
  }
  // Authorized and route allowed: render children
  return children;
}

export default function App() {
  const { user, login, logout, isLoading, tokens, authData } = useContext(AuthContext);
  const { selectedPractice } = usePractice();
  const featureFlags = useFeatureFlags();
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [anchorEl, setAnchorEl] = useState(null);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const [sprCount, setSprCount] = useState(0);
  const apiBase = process.env.REACT_APP_PORTAL_API_URL || '';
  useEffect(() => {
    if (!tokens?.id_token || user?.role !== 'Manager') {
      setSprCount(0);
      return;
    }
    // Function to fetch current count of submitted setup practice requests
    async function fetchSprCount() {
      try {
        const response = await fetch(`${apiBase}/setup-practice-requests/`, {
          headers: { Authorization: `Bearer ${tokens.id_token}` },
        });
        if (!response.ok) {
          console.error('Failed to fetch setup practice requests count');
          return;
        }
        const data = await response.json();
        const count = Array.isArray(data.items)
          ? data.items.filter(request => request.status === 'Submitted').length
          : 0;
        setSprCount(count);
      } catch (error) {
        console.error('Error fetching setup practice requests count:', error);
      }
    }
    // Initial fetch
    fetchSprCount();
    // Listen for updates when requests are approved or re-submitted
    const handleSprUpdate = () => fetchSprCount();
    window.addEventListener('sprCountChanged', handleSprUpdate);
    return () => {
      window.removeEventListener('sprCountChanged', handleSprUpdate);
    };
  }, [tokens, user, apiBase]);

  const menuItems = getMenuItems(user, authData, selectedPractice, featureFlags);
  const menuItemsWithBadges = menuItems.map(item =>
    item.text === 'Setup practice requests'
      ? { ...item, badgeContent: sprCount }
      : item
  );
  // Number of pending invitations for badge notification
  const invCount = authData?.invitations?.length || 0;
  const showDrawer = Boolean(user);

  // Debug logging
  console.log('App - User:', user);
  console.log('App - Tokens:', tokens);
  console.log('App - AuthData:', authData);
  console.log('App - Menu Items:', menuItems);

  return (
    <Router>
      <AuthContext.Provider value={{ user, login, logout, isLoading, tokens, authData }}>
        <Box sx={{ display: 'flex' }}>
            <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
              <Toolbar>
                {showDrawer && (
                  <IconButton
                    color="inherit"
                    aria-label="open drawer"
                    edge="start"
                    onClick={handleDrawerToggle}
                    sx={{ mr: 2, display: { sm: 'none' } }}
                  >
                    <MenuIcon />
                  </IconButton>
                )}
                <Typography
                  variant="h6"
                  noWrap
                  component={Link}
                  to="/"
                  sx={{
                    textTransform: 'none',
                    color: '#000000 !important',
                    fontSize: '2rem',
                    textDecoration: 'none',
                  }}
                >
                  VeraBIND
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                {user?.role === 'Personnel' && (
                  <Box sx={{ mr: 2 }}>
                    <PracticeSelector />
                  </Box>
                )}
                {user && (
                  <>
                    <IconButton
                      size="large"
                      edge="end"
                      aria-label="account of current user"
                      aria-controls="app-menu"
                      aria-haspopup="true"
                      onClick={handleMenu}
                      color="inherit"
                    >
                      {invCount > 0 ? (
                        <Badge badgeContent={invCount} color="error">
                          <AccountCircle />
                        </Badge>
                      ) : (
                        <AccountCircle />
                      )}
                    </IconButton>
                    <Menu
                      id="app-menu"
                      anchorEl={anchorEl}
                      anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                      }}
                      keepMounted
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                      }}
                      open={Boolean(anchorEl)}
                      onClose={handleClose}
                    >
                      <MenuItem component={Link} to="/profile" onClick={handleClose} sx={{ color: '#000000' }}>
                        <ListItemIcon>
                          {invCount > 0 ? (
                            <Badge badgeContent={invCount} color="error">
                              <AccountCircle fontSize="small" />
                            </Badge>
                          ) : (
                            <AccountCircle fontSize="small" />
                          )}
                        </ListItemIcon>
                        <ListItemText primary="Profile" sx={{ color: '#000000' }} />
                      </MenuItem>
                      <MenuItem onClick={() => { handleClose(); logout(); }} sx={{ color: '#000000' }}>
                        <ListItemIcon>
                          <LogoutIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Logout" sx={{ color: '#000000' }} />
                      </MenuItem>
                    </Menu>
                  </>
                )}
              </Toolbar>
            </AppBar>
            {showDrawer && (
              <Drawer
                variant={isMobile ? 'temporary' : 'permanent'}
                open={isMobile ? mobileOpen : true}
                onClose={handleDrawerToggle}
                sx={{
                  width: isMobile ? '100%' : 240,
                  flexShrink: 0,
                  '& .MuiDrawer-paper': {
                    width: isMobile ? '100%' : 240,
                    boxSizing: 'border-box',
                  },
                }}
              >
                <Toolbar />
                <DrawerContent menuItems={menuItemsWithBadges} />
              </Drawer>
            )}
            <Box
              component="main"
              sx={{
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                minHeight: '100vh',
                backgroundColor: 'white',
              }}
            >
              <Toolbar />
              <Box
                sx={{
                  flexGrow: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  mt: { xs: 2, md: 3 },
                  mb: { xs: 6, md: 9 },
                  px: { xs: 2, md: 3 },
                  backgroundColor: 'white',
                }}
              >
                <Container
                  disableGutters
                  sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', backgroundColor: 'white' }}
                >
                  <Routes>
                    <Route path="/logged_out" element={<LoggedOut />} />
                    <Route
                      path="/agreements"
                      element={
                        <PrivateRoute>
                          <Agreements />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/practice/:tab?"
                      element={
                        <PrivateRoute>
                          <Practice />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/users"
                      element={
                        <PrivateRoute>
                          <Users />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/products"
                      element={
                        <PrivateRoute>
                          <Products />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/organizations"
                      element={
                        <PrivateRoute>
                          <Organizations />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/organizations/:organization_id/users/:tab?"
                      element={
                        <PrivateRoute>
                          <OrganizationUsersPage />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/orders"
                      element={
                        <PrivateRoute>
                          <Orders />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/new-order"
                      element={
                        <PrivateRoute>
                          <NewOrder />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/purchase-kits"
                      element={
                        <PrivateRoute>
                          <PurchaseKits />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/patients"
                      element={
                        <PrivateRoute>
                          <Patients />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/results"
                      element={
                        <PrivateRoute>
                          <Results />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/new-user"
                      element={
                        <PrivateRoute>
                          <NewUser />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/setup-practice"
                      element={
                        <PrivateRoute>
                          <SetupPractice />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/setup-practice-requests"
                      element={
                        <PrivateRoute>
                          <SetupPracticeRequests />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/form-templates"
                      element={
                        <PrivateRoute>
                          <FormTemplates />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/collections"
                      element={
                        <PrivateRoute>
                          <Collections />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/all-transactions"
                      element={
                        <PrivateRoute>
                          <AllTransactions />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/transactions"
                      element={
                        <PrivateRoute>
                          <Transactions />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/collections/:collectionEventId"
                      element={
                        <PrivateRoute>
                          <CollectionEventDetail />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      // Organization Users page with optional tab sub-path
                      path="/orgs-users/:tab?"
                      element={
                        <PrivateRoute>
                          <OrgsUsers />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/profile"
                      element={
                        <PrivateRoute>
                          <Profile />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/disabled"
                      element={
                        <PrivateRoute>
                          <DisabledUser />
                        </PrivateRoute>
                      }
                    />
                    <Route
                      path="/"
                      element={
                        user ? (
                          user.role === 'Disabled' ? (
                            <Navigate to="/disabled" replace />
                          ) : user.role === 'Unassigned' ? (
                            <Navigate to="/new-user" replace />
                          ) : ['PendingProvider', 'PendingOfficeStaff'].includes(user.role) ? (
                            <Navigate to="/setup-practice" replace />
                          ) : (
                            <Home />
                          )
                        ) : (
                          <PublicHome onLogin={login} />
                        )
                      }
                    />
                  </Routes>
                </Container>
              </Box>
              <Footer />
            </Box>
          </Box>
      </AuthContext.Provider>
    </Router>
  );
}
